import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderTree, Folder, FileText } from "lucide-react";
import { toast } from "sonner";

const Preview = ({ path }) => (
  <div className="rounded-lg border border-border bg-muted/30 p-3 font-mono text-xs space-y-1" data-testid="drive-folder-preview">
    {path.map((p, i) => {
      const file = i === path.length - 1;
      const Icon = file ? FileText : Folder;
      return (
        <div key={i} className="flex items-center gap-1.5" style={{ paddingLeft: i * 14 }}>
          <Icon className={`w-3.5 h-3.5 shrink-0 ${file ? "text-muted-foreground" : "text-amber-500"}`} />
          <span className="truncate">{p}</span>
        </div>
      );
    })}
  </div>
);

export const DriveFolders = ({ cfg, onSaved }) => {
  const [root, setRoot] = useState(cfg.root_folder_name);
  const [tpl, setTpl] = useState(cfg.folder_template);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/drive/preview", { folder_template: tpl })
        .then(({ data }) => { setPreview(data.path); setErr(null); })
        .catch((e) => setErr(e.response?.data?.detail || "Modelo inválido"));
    }, 300);
    return () => clearTimeout(t);
  }, [tpl, cfg.root_folder_name]);

  const insert = (token) => setTpl((v) => `${v}${v && !v.endsWith("/") && !v.endsWith(" ") ? " " : ""}{${token}}`);

  const save = async () => {
    try {
      if (root.trim() !== cfg.root_folder_name) await api.put("/drive/folder", { root_folder_name: root });
      const { data } = await api.put("/drive/config", { folder_template: tpl });
      toast.success("Organização das pastas salva");
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
  };

  return (
    <div className="space-y-3" data-testid="drive-folders">
      <div className="text-sm font-semibold flex items-center gap-2"><FolderTree className="w-4 h-4 text-emerald-600" /> Organização das pastas</div>
      <div>
        <Label className="text-xs">Pasta principal no Drive</Label>
        <Input value={root} onChange={(e) => setRoot(e.target.value)} className="h-9 mt-1" data-testid="drive-folder-input" />
      </div>
      <div>
        <Label className="text-xs">Modelos prontos</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {cfg.presets.map((p) => (
            <button key={p.template} type="button" onClick={() => setTpl(p.template)} data-testid="drive-preset"
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${tpl === p.template ? "bg-emerald-600 text-white border-emerald-600" : "border-border hover:bg-muted"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs">Estrutura personalizada (use / para criar subpastas)</Label>
        <Input value={tpl} onChange={(e) => setTpl(e.target.value)} className="h-9 mt-1 font-mono text-xs" data-testid="drive-template-input" />
        {err && <div className="text-xs text-destructive mt-1" data-testid="drive-template-error">{err}</div>}
        <div className="flex flex-wrap gap-1 mt-2">
          {Object.entries(cfg.tokens).map(([k, label]) => (
            <button key={k} type="button" onClick={() => insert(k)} title={label} data-testid={`drive-token-${k}`}
                    className="text-[11px] font-mono px-2 py-0.5 rounded bg-muted hover:bg-emerald-100 dark:hover:bg-emerald-950">{`{${k}}`}</button>
          ))}
          <button type="button" onClick={() => setTpl((v) => `${v}/`)} className="text-[11px] font-mono px-2 py-0.5 rounded bg-muted hover:bg-emerald-100" data-testid="drive-token-slash">/ subpasta</button>
        </div>
      </div>
      {preview && !err && <div><Label className="text-xs">Exemplo de onde um arquivo será salvo</Label><div className="mt-1"><Preview path={[root.trim() || "…", ...preview.slice(1)]} /></div></div>}
      <Button size="sm" onClick={save} disabled={!!err} className="bg-emerald-600 hover:bg-emerald-700" data-testid="drive-folders-save">Salvar organização</Button>
      <p className="text-[11px] text-muted-foreground">A nova organização vale para os próximos envios. Arquivos já enviados permanecem onde estão.</p>
    </div>
  );
};
